import {
  useCallback, useRef, useState,
  type KeyboardEvent, type PointerEvent, type RefObject,
} from 'react'
import { readNumber, writeNumber } from './prefs'

/** A DIVIDER SOMEBODY CAN PULL, between a list and what it opens.
 *
 *  The operator, looking at the Feedback tab on a full screen (5 Oct 2026):
 *  "there is two columns ... I would like to be able to pull that and adjust
 *  which column should be bigger."
 *
 *  THE WIDTH IS A TASTE, NOT A RECORD, so it is remembered per browser
 *  (`lib/prefs`) like the theme and a sheet's columns, under a key of the
 *  screen's own.
 *
 *  THE PAGE'S STYLESHEET KEEPS THE LAST WORD. This hands back a number and
 *  the page puts it in a CSS variable; the stylesheet clamps it against the
 *  window it is drawn in. So a width dragged on a wide monitor cannot leave
 *  the other column with nothing on a narrow one, and at phone width -- one
 *  column at a time -- the variable is simply not read.
 *
 *  THREE WAYS TO MOVE IT, because a mouse is not the only hand: drag it;
 *  arrow keys when it has the focus (Home and End for the ends); and a
 *  double click, or Enter, puts it back where the screen would have had it. */
export function useSplit(key: string, {
  min = 300, other = 420, gutter = 14, step = 24,
}: {
  /** The narrowest the first column may be dragged. */
  min?: number
  /** What must be left for the second column. */
  other?: number
  /** The width of the divider's own track. */
  gutter?: number
  /** How far one press of an arrow key moves it. */
  step?: number
} = {}): {
  box: RefObject<HTMLDivElement>
  width: number | null
  dragging: boolean
  handle: Record<string, unknown>
} {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number | null>(() => readNumber(key, min, 4000))
  const [dragging, setDragging] = useState(false)
  /** Read by the handlers, which outlive the render they were made in. */
  const live = useRef<{ width: number | null; dragging: boolean }>({ width, dragging: false })

  const room = useCallback(() => {
    const el = box.current
    if (!el) return { left: 0, max: 4000, now: min }
    const r = el.getBoundingClientRect()
    const cs = getComputedStyle(el)
    const padL = parseFloat(cs.paddingLeft) || 0
    const padR = parseFloat(cs.paddingRight) || 0
    const first = el.firstElementChild?.getBoundingClientRect().width ?? min
    return {
      left: r.left + padL,
      max: Math.max(min, r.width - padL - padR - gutter - other),
      now: Math.round(first),
    }
  }, [min, other, gutter])

  const place = useCallback((px: number) => {
    const next = Math.round(Math.min(room().max, Math.max(min, px)))
    live.current.width = next
    setWidth(next)
  }, [room, min])

  const reset = useCallback(() => {
    live.current.width = null
    setWidth(null)
    writeNumber(key, null)
  }, [key])

  const finish = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (!live.current.dragging) return
    live.current.dragging = false
    setDragging(false)
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* already gone */ }
    writeNumber(key, live.current.width)
  }, [key])

  const handle = {
    role: 'separator',
    'aria-orientation': 'vertical',
    'aria-label': 'Resize the two columns',
    'aria-valuemin': min,
    'aria-valuenow': width ?? undefined,
    tabIndex: 0,
    title: 'Drag to resize. Double-click to put it back.',
    onPointerDown: (e: PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      e.preventDefault()
      // Captured, so the drag keeps going when the pointer leaves the thin
      // strip -- which it does on the first pixel.
      try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* not a real pointer */ }
      live.current.dragging = true
      setDragging(true)
    },
    onPointerMove: (e: PointerEvent<HTMLDivElement>) => {
      if (!live.current.dragging) return
      place(e.clientX - room().left - gutter / 2)
    },
    onPointerUp: finish,
    onPointerCancel: finish,
    onLostPointerCapture: finish,
    onDoubleClick: reset,
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      const from = live.current.width ?? room().now
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault()
        place(from + (e.key === 'ArrowLeft' ? -step : step))
        writeNumber(key, live.current.width)
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault()
        place(e.key === 'Home' ? min : 4000)
        writeNumber(key, live.current.width)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        reset()
      }
    },
  }

  return { box, width, dragging, handle }
}
